import {
  Card,
  CardContent,
  CardHeader,
  Collapse,
  Stack,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React, { useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { TableTaskStatus } from '@components/DataTable';
import Icon from '@components/Icon/Icon';

import { TaskPriorityMap } from '@utils/curator-task';
import { formatDate } from '@utils/format';

import type { CuratorTaskModel } from 'generated/types';

import { TaskAssociations } from './TaskAssociations';

const messages = defineMessages({
  priority: {
    id: 'taskCard.priority',
    defaultMessage: 'Priority: {priority}',
  },
  description: {
    id: 'taskCard.description',
    defaultMessage: 'Description: {description}',
  },
  recordDescription: {
    id: 'taskCard.recordDescription',
    defaultMessage: 'Record: {recordDescription}',
  },
  dueDate: {
    id: 'taskCard.dueDate',
    defaultMessage: 'Due: {due_date}',
  },
});

interface TaskCardProps {
  task: Pick<
    CuratorTaskModel,
    | 'description'
    | 'due_date'
    | 'name'
    | 'priority'
    | 'recordDescription'
    | 'route'
    | 'curator_task_id'
    | 'status'
  >;
}

export default function TaskCard({ task }: TaskCardProps) {
  const { formatMessage } = useIntl();
  const [collapsed, setCollapsed] = useState(false);
  const { palette } = useTheme();

  const handleToggleExpand = () => {
    setCollapsed(!collapsed);
  };

  const { description, due_date, name, priority, recordDescription, route } =
    task;
  const dueDate = formatDate({ date: due_date });

  return (
    <Card
      sx={{
        '.MuiCard-root': {
          backgroundColor: palette.grey.A200,
        },
      }}
    >
      <CardHeader
        avatar={<TableTaskStatus size="1x" task={task} />}
        action={
          <Icon
            name={collapsed ? 'chevron-up' : 'chevron-down'}
            onClick={handleToggleExpand}
            type="fas"
          />
        }
        title={
          <Typography color={palette.text.primary} variant="body1">
            {name}
          </Typography>
        }
        subheader={
          <Stack spacing={1}>
            <div>
              {formatMessage(messages.dueDate, {
                due_date: dueDate,
              })}
            </div>
            <TaskAssociations route={route} />
          </Stack>
        }
        slotProps={{
          title: { sx: { color: (theme) => theme.palette.primary.main } },
        }}
      />
      <Collapse in={collapsed} timeout="auto" unmountOnExit>
        <CardContent sx={{ p: 0.5 }}>
          <Typography variant="body2">
            {formatMessage(messages.priority, {
              priority: TaskPriorityMap[priority].text,
            })}
          </Typography>
          <Typography variant="body2">
            {formatMessage(messages.description, {
              description: description ?? '-',
            })}
          </Typography>
          <Typography variant="body2">
            {recordDescription
              ? formatMessage(messages.recordDescription, {
                  recordDescription: `${recordDescription}`,
                })
              : null}
          </Typography>
        </CardContent>
      </Collapse>
    </Card>
  );
}
